import { Container } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Checkbox,
  Divider,
  Group,
  PinInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import {
  AppDomain,
  Colors,
  HttpSuccessCodesObj,
  LogoSmall,
  ValidOtpLength,
} from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getBrowser,
  getDevice,
  getIpAddress,
  parseString,
  validateEmail,
} from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";

import { PerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";

const Login: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const emailForm = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) =>
        validateEmail(value) ? null : "Please enter a valid email address",
    },
  });
  const otpForm = useForm({
    initialValues: {
      otp: "",
    },
    validate: {
      otp: (value) =>
        value && value.length === ValidOtpLength
          ? null
          : `Please enter a ${ValidOtpLength}-digit OTP`,
    },
  });
  const [isKeepAdminLoggedIn, setKeepAdminLoggedIn] = useState(false);

  // UI state
  const [isRequestingOTP, setRequestingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Simulation state
  const [simulatedOTP, setSimulatedOTP] = useState("");

  // Countdown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const [token, setToken] = useState("");

  const RequestOTP = async () => {
    setRequestingOTP(true);
    const { email } = emailForm.values;
    const browser = getBrowser();
    const device = getDevice();
    const ipAddress = await getIpAddress();
    const r = await PerformRequest<NonPaginatedResponse<{ tokenID: string }>>({
      method: Endpoints.GetRequestOTP().method,
      route: Endpoints.GetRequestOTP().route,
      data: {
        email: parseString(email, true),
        browser: browser,
        device: device,
        ip: ipAddress,
      },
      callback: () => {
        setRequestingOTP(false);
      },
    });
    setRequestingOTP(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Created) {
        setToken(r.data?.data?.tokenID ?? "");
        DefaultSuccessNotification("OTP sent to your email");
        setOtpSent(true);
        setCanResend(false);
        setResendTimer(60);
      } else {
        DefaultErrorNotification(r?.message ?? "Could not send OTP");
      }
    }
  };
  // Request OTP
  const HandleRequestOTP = async () => {
    const { hasErrors } = emailForm.validate();
    if (!hasErrors) {
      RequestOTP();
    }
  };

  // Resend OTP
  const HandleResendOTP = async () => {
    if (!canResend) {
      return;
    } else {
      RequestOTP();
    }
  };

  // Verify OTP
  const HandleVerifyOTP = async () => {
    const { hasErrors } = otpForm.validate();
    if (!hasErrors) {
      const { otp } = otpForm.values;
      const { email } = emailForm.values;
      setIsVerifyingOTP(true);
      const browser = getBrowser();
      const device = getDevice();
      const ipAddress = await getIpAddress();
      const r = await PerformRequest<NonPaginatedResponse<{ token: string }>>({
        method: Endpoints.GetVerifyOTP(token).method,
        route: Endpoints.GetVerifyOTP(token).route,
        data: {
          otp: parseString(otp, true),
          email: parseString(email, true),
          browser: browser,
          device: device,
          ip: ipAddress,
          isKeepUserLoggedIn: isKeepAdminLoggedIn,
        },
        callback: () => {
          setIsVerifyingOTP(false);
        },
      });
      setIsVerifyingOTP(false);
      if (r) {
        if (r.status === HttpSuccessCodesObj.Success) {
          DefaultSuccessNotification("OTP verified");
          localStorage.setItem("t", r?.data?.data?.token ?? "");
          navigate("/d");
        } else {
          DefaultErrorNotification(r?.message ?? "Could not verify OTP");
        }
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setOtpSent(false);
    otpForm.reset();
    setResendTimer(0);
    setCanResend(true);
    setSimulatedOTP("");
  };

  const inputProps = {
    className: "input",
    spellCheck: false,
    radius: "sm",
    size: "md",
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <Container maxWidth="sm">
      <div className="login-container">
        <Stack gap="xl" align="center" w="100%" py={40}>
          {/* Header */}
          <Stack gap="xs" align="center">
            <img
              src={LogoSmall}
              alt=""
              className="logo"
              style={{ width: 60, height: 60 }}
            />
            <Text fw={600} fz={24} c={Colors.BlackPrimary}>
              Admin Dashboard
            </Text>
            <Text c={Colors.BlackPrimary} fz={16} opacity={0.7}>
              Sign in to continue
            </Text>
          </Stack>

          {/* Form */}
          <Stack gap="lg" w="100%" style={{ maxWidth: 400 }}>
            <TextInput
              type="email"
              label="Email Address"
              name="email"
              placeholder={`you@${AppDomain}`}
              {...emailForm.getInputProps("email")}
              disabled={otpSent}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !otpSent) {
                  HandleRequestOTP();
                }
              }}
              {...inputProps}
            />

            {!otpSent ? (
              <Button
                fullWidth
                onClick={HandleRequestOTP}
                color={Colors.PurplePrimary}
                loading={isRequestingOTP}
                disabled={!validateEmail(emailForm.values?.email, true)}
                radius="sm"
                size="md"
                h={48}
              >
                Send OTP
              </Button>
            ) : (
              <Stack gap="lg">
                <Divider />

                <Stack gap="md" align="center">
                  <Text
                    c={Colors.BlackPrimary}
                    fz={15}
                    ta="center"
                    opacity={0.8}
                  >
                    Enter the 6-digit code sent to your email
                  </Text>

                  <PinInput
                    length={6}
                    placeholder="○"
                    size="md"
                    radius="sm"
                    {...otpForm.getInputProps("otp")}
                  />
                </Stack>

                <Group justify="space-between" w="100%">
                  <Text
                    c={canResend ? Colors.PurplePrimary : Colors.BlackPrimary}
                    fz={14}
                    style={{
                      cursor: canResend ? "pointer" : "default",
                      opacity: canResend ? 1 : 0.6,
                    }}
                    onClick={canResend ? HandleResendOTP : undefined}
                  >
                    {canResend
                      ? "Resend code"
                      : `Resend in ${formatTime(resendTimer)}`}
                  </Text>

                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={resetForm}
                    radius="sm"
                    color="gray"
                  >
                    Change email
                  </Button>
                </Group>

                <Divider />

                <Group gap="xs">
                  <Checkbox
                    checked={isKeepAdminLoggedIn}
                    onChange={(e) => setKeepAdminLoggedIn(e.target.checked)}
                    radius="sm"
                  />
                  <Text
                    onClick={() => setKeepAdminLoggedIn(!isKeepAdminLoggedIn)}
                    c={Colors.BlackPrimary}
                    fz={14}
                    style={{ cursor: "pointer" }}
                  >
                    Keep me signed in
                  </Text>
                </Group>

                <Button
                  fullWidth
                  onClick={HandleVerifyOTP}
                  color={Colors.PurplePrimary}
                  loading={isVerifyingOTP}
                  disabled={
                    otpForm.values?.otp?.length !== ValidOtpLength ||
                    isVerifyingOTP
                  }
                  radius="sm"
                  size="md"
                  h={48}
                >
                  Sign In
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </div>
    </Container>
  );
};

export default Login;
